import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import COLOR from 'constants/color';
import { Calendar } from 'assets/icons/revamp';
import Card from 'components/ui/Card';
import { t } from 'services/i18n.service';
import { updateMoreOptionDaysOfWeek } from 'store/reducer/modal/slice';
import { DaysOfWeek } from 'enums';
import { ALL_DAYS_OF_WEEK } from 'constants/daysOfWeek';
import { dayOfWeekAbbrevLabels } from 'utils/settings.util';
import BaseButton from 'components/ui/BaseButton';
import { moreOptionModalActivitySelector } from 'store/reducer/modal/more-options.modal.selectors';
import { computeDaysOfWeekAfterWeeklyToggle } from 'utils/daysOfWeek.util';

const WeeklySchedule: React.FC = () => {
  const dispatch = useAppDispatch();
  const activity = useAppSelectorShallow(moreOptionModalActivitySelector);

  const activityDays = (activity?.days_of_week || []) as DaysOfWeek[];

  const handleDayToggle = (day: DaysOfWeek | 'Weekdays' | 'Weekend') => {
    dispatch(
      updateMoreOptionDaysOfWeek(
        computeDaysOfWeekAfterWeeklyToggle(activityDays, day)
      )
    );
  };

  const handleSelectAll = () => {
    dispatch(updateMoreOptionDaysOfWeek([...ALL_DAYS_OF_WEEK]));
  };

  return (
    <Card className='flex flex-col ~gap-3/4'>
      <div className='flex items-start ~gap-2/3'>
        <Calendar
          className='~w-4/6 ~h-4/6 mt-0.5'
          fill={COLOR.PRIMARY.LIGHT_600}
          fillDarkMode={COLOR.PRIMARY.DARK_600}
        />
        <div className='flex-1 flex flex-col ~gap-1/2'>
          <h3 className='~text-xs/base font-semibold text-black dark:text-white'>
            {t('tabs_revamp.timing_tab.weekly_schedule')}
          </h3>
          <p className='~text-xs/sm text-gray-600 dark:text-gray-400'>
            {t('tabs_revamp.timing_tab.weekly_schedule_description')}
          </p>
        </div>
      </div>
      <div className='flex flex-col ~gap-2/3 ~ml-0/0'>
        <div className='flex flex-wrap items-center ~gap-2/3'>
          {ALL_DAYS_OF_WEEK.map((day) => {
            const isSelected = activityDays.includes(day);
            return (
              <button
                key={day}
                onClick={() => handleDayToggle(day)}
                className={`~px-2/4 ~py-1/2 rounded-md transition-colors ~text-[0.65rem]/xs font-medium border ${
                  isSelected
                    ? 'bg-primary-700 text-white border-primary-300 dark:bg-primary-800 dark:border-primary-600'
                    : 'bg-white dark:bg-primary-dark-50 border-primary-dark-50 dark:border-primary-900 text-black dark:text-white hover:bg-primary-100 dark:hover:bg-primary-700'
                }`}>
                {t(dayOfWeekAbbrevLabels[day])}
              </button>
            );
          })}
        </div>
        <div className='flex items-center ~gap-3/4'>
          <BaseButton
            onClick={() => handleDayToggle('Weekdays')}
            variant='link'
            size='xs'
            title={t('tabs_revamp.timing_tab.select_weekdays')}
          />
          <BaseButton
            onClick={() => handleDayToggle('Weekend')}
            variant='link'
            size='xs'
            title={t('tabs_revamp.timing_tab.select_weekend')}
          />
          <BaseButton
            onClick={handleSelectAll}
            variant='link'
            size='xs'
            title={t('tabs_revamp.timing_tab.select_all')}
          />
          <BaseButton
            onClick={() => dispatch(updateMoreOptionDaysOfWeek([]))}
            variant='link'
            size='xs'
            title={t('tabs_revamp.timing_tab.clear_all')}
          />
        </div>
      </div>
    </Card>
  );
};

export default WeeklySchedule;
